<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
    import ModalOverlay from '../modal/ModalOverlay.svelte';
    import Dialog from './Dialog.svelte';
    import Alert from './presets/AlertDialog.svelte';
    import Confirm from './presets/ConfirmDialog.svelte';
    import { createEventDispatcher } from 'svelte';
    import { calculateAbsolutePosition } from '@oat-sa-private/ui-core/dom/positioning.js';
    import { breakpoints } from '@oat-sa-private/ui-identity';

    const dispatch = createEventDispatcher();

    const dialogTypes = {
        dialog: Dialog, // fully customisable
        alert: Alert, // preset with OK button
        confirm: Confirm // preset with OK + Cancel buttons
    };

    export let type = 'dialog';
    export let open = false;
    export let heading = '';
    export let message = '';
    export let buttons = [];
    export let buttonsAlignment = 'end';
    export let icon = '';
    export let iconStatus = 'neutral';
    export let headingCentered = false;

    // For anchored positioning mode
    export let caller = null;
    export let anchoring = 'top left';

    if (!(caller instanceof HTMLElement)) {
        caller = null;
    }
    if (!anchoring || !anchoring.match(/^(top|bottom)\s+(left|right)$/)) {
        anchoring = 'top left';
    }
    if (!['end', 'start', 'center'].includes(buttonsAlignment)) {
        buttonsAlignment = 'end';
    }

    export let disableClosing = false; // prevents closing by clicking outside
    export let disableEscape = false; // prevents closing by escape key

    if (type === 'alert' || type === 'confirm') {
        disableClosing = true;
        disableEscape = true;
    }

    // DOM elements
    let dialogWrapperElement;
    let previouslyFocused;

    // Style holder
    let absoluteStyle;

    let windowWidth;
    let windowHeight;

    /**
     * @fires open when it opens
     */
    $: if (open) {
        dispatch('open');
    }

    // When ModalDialog opens, focus button with initialFocus=true, or, if none such, the first button
    $: if (open && dialogWrapperElement) {
        previouslyFocused = document && document.activeElement;

        if (buttons.length > 0) {
            let index = buttons.findIndex(btn => btn.initialFocus);
            if (index < 0) {
                index = 0;
            }
            const buttonElements = dialogWrapperElement.querySelectorAll('.dialog > .actions button');
            buttonElements[index].focus();
        }
    }

    // Positioning (tablet & desktop only: we won't allow it if the window width or height are likely to be too small)
    $: if (
        caller &&
        anchoring &&
        dialogWrapperElement &&
        windowWidth > breakpoints.width.medium &&
        windowHeight > breakpoints.height.small
    ) {
        let anchorRect = caller.getBoundingClientRect();
        let { top, left, bottom, right } = calculateAbsolutePosition({
            anchorRect,
            documentDimensions: {
                scrollTop: document.documentElement.scrollTop,
                scrollLeft: document.documentElement.scrollLeft,
                clientWidth: document.documentElement.clientWidth,
                clientHeight: document.documentElement.clientHeight
            },
            anchoring,
            offset: {
                x: 0,
                y: 16
            }
        });
        absoluteStyle = `top:${top}; bottom:${bottom}; left:${left}; right:${right}; transform:none ; position:absolute;`;
    }

    /**
     * Close Modal
     * @fires close when it closes
     */
    function close() {
        open = false;
        dispatch('close');

        if (previouslyFocused) {
            previouslyFocused.focus();
        }
    }

    /**
     * Handle keyboard input:
     * - Escape to close
     * - Tab key focus should be trapped in Dialog
     * @param {KeyEvent} e
     */
    function handleKeydown(e) {
        if (!open) {
            return;
        }

        if (e.key === 'Escape' && !disableEscape) {
            close();
            return;
        }

        if (e.key === 'Tab') {
            // trap focus
            const nodes = dialogWrapperElement.querySelectorAll('*');
            const tabbable = Array.from(nodes).filter(n => n.tabIndex !== -1);

            let index = tabbable.indexOf(document.activeElement);
            if (index === -1 && e.shiftKey) index = 0;

            index += tabbable.length + (e.shiftKey ? -1 : 1);
            index %= tabbable.length;

            if (tabbable[index]) {
                tabbable[index].focus();
            }
            e.preventDefault();
        }
    }
</script>

<style>
    .modal-positioning {
        position: relative;
        overflow: auto;
        z-index: var(--layer-5);

        /* larger than mobile */
        min-width: 50rem;
        max-width: 75rem;

        &:focus,
        &:active {
            outline: none;
        }

        /* mobile */
        @media screen and (--mq-maxwidth-small) {
            min-width: unset;
            max-width: calc(100% - 4rem);
            max-height: calc(100vh - 4rem);
        }
    }
</style>

<svelte:window bind:innerWidth={windowWidth} bind:innerHeight={windowHeight} />

{#if open}
    <ModalOverlay {disableClosing} on:close={close}>
        <div
            class="modal-positioning"
            aria-modal="true"
            bind:this={dialogWrapperElement}
            style={absoluteStyle}
            on:keydown={handleKeydown}>
            <svelte:component
                this={dialogTypes[type]}
                mode="modal"
                {heading}
                {headingCentered}
                {message}
                {buttons}
                {buttonsAlignment}
                {icon}
                {iconStatus}
                on:action
                on:close={close}>
                <slot></slot>
            </svelte:component>
        </div>
    </ModalOverlay>
{/if}
